import { useEffect, useLayoutEffect, useRef, useState } from "react";
import desktopVideo from "@/assets/auth/salefish-login-desktop.mp4";
import mobileVideo from "@/assets/auth/salefish-login-mobile.mp4";
import desktopVideoHevc from "@/assets/auth/salefish-login-desktop-hevc.mp4";
import mobileVideoHevc from "@/assets/auth/salefish-login-mobile-hevc.mp4";
import desktopPoster from "@/assets/auth/salefish-login-desktop-poster.jpg";
import mobilePoster from "@/assets/auth/salefish-login-mobile-poster.jpg";

/**
 * The film behind every sign-in screen (Andrew, 2026-10-01): a 32-second loop
 * of homes, condos and sales centres, in full colour under a dark violet wash
 * (`.auth-backdrop-mask` in index.css). Two cuts of the same edit: 16:9 for a
 * landscape window and a 9:16 crop framed shot by shot for a phone. Each ends
 * on the frame it starts from, so `loop` has no seam.
 *
 * Each cut ships twice. The source clips carry AI-render grain, so they were
 * denoised before encoding (nlmeans + a light temporal pass, 2026-10-01 —
 * "a little too grainy"); a 10-bit HEVC copy then serves every browser that
 * says it can play one (all Apple devices: 5.5 MB desktop / 2.5 MB phone, and
 * 10-bit keeps the dusk skies from banding), and H.264 serves the rest
 * (7.7 / 3.1 MB).
 *
 * It must never cost the sign-in anything, so:
 *   - the poster paints first and is the whole backdrop until the film is
 *     actually PLAYING; the film fades in over it, so a failed or refused
 *     play (Low Power Mode blocks autoplay) simply leaves the still;
 *   - the film is not requested until the page has loaded and gone idle, so
 *     it never competes with the sign-in's own script, styles and fonts;
 *   - reduced motion, Data Saver and a 2G-class connection get the still and
 *     no download at all;
 *   - it pauses while the tab is hidden, and resumes where it was when the
 *     reader moves between sign-in screens (sign in → 2FA → forgot password).
 *
 * Both files are imported, so they ship content-hashed under /assets/ with a
 * year's immutable cache — which an Apple device never used: Safari fetches
 * media with Range requests and WebKit's disk cache does not keep a ranged
 * response, so the film was downloaded again on every sign-in screen. The
 * service worker now keeps one whole copy and answers each range with a 206
 * cut from it (`MEDIA_CACHE` in public/service-worker.js), which survives
 * deploys because the hashed filename is already the version.
 *
 * While it is mounted, `html.auth-backdrop-on` repaints the document canvas
 * and the iOS top-edge sample in the backdrop's ink, so the status-bar strip
 * of an installed app and the edge below it match the film's dark top. Set in
 * a LAYOUT effect: WebKit reads the edge colour before the first tap, and the
 * class has to exist by the first paint (standing rule, ui/top-edge-sample).
 */

const PORTRAIT = "(orientation: portrait)";

/** HEVC Main 10, level 4.1 — what the -hevc files are. */
const HEVC_MAIN10 = 'video/mp4; codecs="hvc1.2.4.L123.B0"';

/** The cut for this screen, in the best codec this browser will play. */
export function pickFilm(portrait: boolean, canPlay: (type: string) => string): string {
  const hevc = canPlay(HEVC_MAIN10) !== "";
  if (portrait) return hevc ? mobileVideoHevc : mobileVideo;
  return hevc ? desktopVideoHevc : desktopVideo;
}

/** Where the loop was when the last sign-in screen unmounted. */
let resumeAt = 0;

type Connection = { saveData?: boolean; effectiveType?: string };

/** The still only: the reader asked for less motion or less data, or the
    connection could not stream a 2–6 MB film without hurting the page. */
export function prefersStill(env: {
  reducedMotion: boolean;
  connection?: Connection | null;
}): boolean {
  if (env.reducedMotion) return true;
  const c = env.connection;
  if (!c) return false;
  return Boolean(c.saveData) || /2g$/.test(c.effectiveType ?? "");
}

export function AuthBackdrop() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add("auth-backdrop-on");
    return () => root.classList.remove("auth-backdrop-on");
  }, []);

  // Choose and attach the film once the page is loaded and idle.
  useEffect(() => {
    const still = prefersStill({
      reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
      connection: (navigator as Navigator & { connection?: Connection }).connection,
    });
    if (still) return;

    let cancelled = false;
    const attach = () => {
      if (cancelled) return;
      const probe = document.createElement("video");
      setSrc(pickFilm(window.matchMedia(PORTRAIT).matches, (t) => probe.canPlayType(t)));
    };
    const whenIdle = () => {
      if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(attach, { timeout: 2000 });
      } else {
        window.setTimeout(attach, 300);
      }
    };
    if (document.readyState === "complete") whenIdle();
    else window.addEventListener("load", whenIdle, { once: true });
    return () => {
      cancelled = true;
      window.removeEventListener("load", whenIdle);
    };
  }, []);

  // Start it, keep it paused while hidden, and remember where it got to.
  useEffect(() => {
    const video = videoRef.current;
    if (!src || !video) return;
    // iOS autoplays only a muted inline video, and React sets `muted` as a
    // property after the element exists — so say it here, before play().
    video.muted = true;
    video.defaultMuted = true;

    const play = () => { video.play().catch(() => {}); };
    const onMeta = () => {
      if (resumeAt > 0 && resumeAt < video.duration) video.currentTime = resumeAt;
      if (!document.hidden) play();
    };
    const onVisibility = () => (document.hidden ? video.pause() : play());

    video.addEventListener("loadedmetadata", onMeta, { once: true });
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      resumeAt = video.currentTime;
      video.removeEventListener("loadedmetadata", onMeta);
      document.removeEventListener("visibilitychange", onVisibility);
      video.pause();
    };
  }, [src]);

  return (
    <div aria-hidden="true" className="auth-backdrop" data-testid="auth-backdrop">
      <picture>
        <source media={PORTRAIT} srcSet={mobilePoster} />
        <img src={desktopPoster} alt="" decoding="async" />
      </picture>
      {src && (
        <video
          ref={videoRef}
          src={src}
          muted
          playsInline
          loop
          preload="auto"
          disablePictureInPicture
          disableRemotePlayback
          data-playing={playing ? "" : undefined}
          onPlaying={() => setPlaying(true)}
        />
      )}
      <div className="auth-backdrop-mask" />
    </div>
  );
}
